import React, { useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import CustomDataGrid from "../../../components/dms_common/CustomDataGrid";
import Header from "../../../components/dms_common/Header";
import FormHeader from "../../../components/dms_common/FormHeader";
import CustomAccordian from "../../../components/dms_common/CustomAccordian";
import { Loader } from "../../../components/loader/Loader";
import Search from "../../../components/dms_common/Search";
import { Button } from "@mui/material";
import {
  outletLedgerDocumentMetaData,
  collectionMetaData2,
  collectionMetaData1,
} from "./OutletLedgerUtils";

import { ReactComponent as DownloadAllIcon } from "../../../assets/image/svg/downloadAllWhite.svg";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert, { AlertProps } from "@mui/material/Alert";
import DateFilter from "../../deliveryConfirmation/DateFilter";
import { OutletLedgerTransaction } from "../../../types/types";
import { fetchOutletLedgerTransactions } from "./outletLedgerService";

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
  props,
  ref
) {
  return <MuiAlert elevation={6} ref={ref} {...props} />;
});

const OutletLedgerDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // Get values from params or location.state
  const outletCode: string = location.state?.outletCode ?? "-";
  const outletName: string = location.state?.outletName ?? "-";
  const lastPaymentDate: string = location.state?.lastPaymentDate ?? "-";
  const lastBilledDate: string = location.state?.lastBilledDate ?? "-";
  const creditAmount: string = location.state?.creditAmount ?? "-";
  const createdOn: string = location.state?.createdOn ?? "-";

  // Prepare info object for CustomAccordian
  const outletInfo = {
    outletCode,
    outletName,
    lastPaymentDate,
    creditAmount:JSON.parse(localStorage.ConfigDictionary).currency + " " + creditAmount,
    lastBilledDate,
    createdOn,
  };

  const [startDate, setStartDate] = useState<Dayjs>(dayjs());
  const [endDate, setEndDate] = useState<Dayjs>(dayjs());
  const [searchText, setSearchText] = useState("");
  const [allData, setAllData] = useState<OutletLedgerTransaction[]>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [loader, setLoader] = useState(false);
  const [isAccordionOpen, setIsAccordionOpen] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const handleDateChange = (newStartDate: Dayjs, newEndDate: Dayjs) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };


  const handleDownloadAll = async () => {
    // TODO: Implement actual download logic
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const fetchAndSaveTransactionData = async()=>{
    setLoader(true);
    const fetchedData:OutletLedgerTransaction[] = await fetchOutletLedgerTransactions(startDate, endDate);
    setAllData(fetchedData);
    setLoader(false);
  }

  useEffect(()=>{
    fetchAndSaveTransactionData();
  },[startDate, endDate])

  useEffect(()=>{
    if(searchText){
      const updatedData = allData.filter((data)=>{
        data.description.toLowerCase().includes(searchText.toLowerCase()) ||
        data.particular.toLowerCase().includes(searchText.toLowerCase()) ||
        data.documentId.toLowerCase().includes(searchText.toLowerCase())
      })
      setFilteredData(updatedData);
    }
  },[searchText, allData])


  return (
    <div className="cash-collection-container">
      <div className="view_order_header_with_search">
        <div className="view_order_header">
          <FormHeader
            parentLabel={"Outlet Ledger"}
            label={"Outlet Details"}
            goBackToParent={() => navigate(-1)}
          />
        </div>
        <div className="view_order_manage_order_search_bar">
          <Search
            searchFunction={setSearchText}
            value={searchText}
          />
        </div>
      </div>
      <div className="main-content-container">
        <div
          className={`left-content-container`}
          style={{
            background: "#fff",
            borderRadius: 8,
            boxShadow: "0 2px 8px #0000000a",
            padding: 1,
            marginBottom: 24,
            width: "100%",
          }}
        >
          <div style={{ width: "100%" }}>
            <CustomAccordian
              expanded={isAccordionOpen}
              onCustomAccordionToggle={setIsAccordionOpen}
              metaData1={collectionMetaData1}
              metaData2={collectionMetaData2}
              data1={outletInfo}
              data2={outletInfo}
              header={`Outlet Code ${outletInfo?.outletCode || ""}`}
              label1={""}
              singleColumnInEachHalf={true}
              isSecondHalf={true}
              statusConfig={null}
              status={""}
            />
          </div>
          <div className="order-details-vo" style={{ marginTop: 24 }}>
            <div className="invoice-header-container" style={{ marginBottom: 16 }}>
              <Header title={"Ledger Details"} />
            </div>
            <div className="controls-container" style={{ marginBottom: 16 }}>
              <div className="manage_order_support_utility_dates_manageOrder" style={{ gap: 16, display: "flex" }}>
                <DateFilter
                  onApply={()=>{}}
                  onReset={()=>{}}
                  onUpdate={handleDateChange}
                  initialDayOffset={0}
                  endDayOffset={0}
                  maxDays={30}
                  futureDateAllowed={false}
                />
                <Button
                  variant="contained"
                  sx={{
                    textTransform: "none",
                    minWidth: "160px",
                    backgroundColor: "var(--dashboard-theme-color)",
                    boxShadow: "none",
                    "&:hover": {
                      boxShadow: "none",
                      background: "var(--dashboard-theme-color)",
                    },
                  }}
                  onClick={handleDownloadAll}
                  disableRipple={snackbarOpen}
                >
                  <DownloadAllIcon style={{ marginRight: "6px" }} />
                  Download All
                </Button>
              </div>
            </div>
            <CustomDataGrid
              rows={filteredData}
              columnsMetaData={outletLedgerDocumentMetaData}
              tableWidth={0.79}
              showShelfLifeFooter={false}
              noDataMessage="No Ledger data found."
              orderStatusConfig={undefined}
            />
          </div>
        </div>
      </div>
      <Snackbar
        open={snackbarOpen}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="info"
          variant="filled"
          sx={{ width: "100%" }}
        >
          Your report will be downloaded shortly.
        </Alert>
      </Snackbar>
    </div>
  );
};

export default OutletLedgerDetails;
